import type { QuestionForStudent } from "@/types";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { OptionButton, type OptionState } from "./OptionButton";
import { ExplanationBox } from "./ExplanationBox";
import { ServerTimer } from "./ServerTimer";

interface Props {
  domanda: QuestionForStudent;
  index: number;
  totale: number;
  secondiRimasti?: number;
  onExpired: () => void;
  // Per chiusa: stato di ciascuna opzione (idle | loading | correct | wrong | disabled)
  optionStates?: OptionState[];
  onSelect: (i: number) => void;
  // Per aperta: testo controllato + submit
  rispostaTesto?: string;
  onChangeTesto?: (v: string) => void;
  onSubmitAperta?: () => void;
  sending?: boolean;
  // Mostrata dopo risposta
  isCorrect?: boolean | null;
}

export function QuestionCard({
  domanda,
  index,
  totale,
  secondiRimasti,
  onExpired,
  optionStates = [],
  onSelect,
  rispostaTesto = "",
  onChangeTesto,
  onSubmitAperta,
  sending = false,
  isCorrect,
}: Props) {
  return (
    <Card className="space-y-5">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Badge color="#2C5F8A">
            Domanda {index + 1}/{totale}
          </Badge>
          {domanda.sezione && (
            <Badge color={domanda.coloreSezione ?? "#5B3E8A"} variant="outline">
              {domanda.sezione}
            </Badge>
          )}
        </div>
        <ServerTimer secondiRimasti={secondiRimasti} onExpired={onExpired} />
      </header>

      <h2 className="text-xl text-primary font-serif leading-snug">
        {domanda.testo}
      </h2>

      {domanda.tipo === "chiusa" && domanda.opzioniShufflate && (
        <div className="space-y-3">
          {domanda.opzioniShufflate.map((op, i) => (
            <OptionButton
              key={i}
              testo={op}
              index={i}
              state={optionStates[i] ?? "idle"}
              onClick={onSelect}
            />
          ))}
        </div>
      )}

      {domanda.tipo === "aperta" && (
        <div className="space-y-3">
          <textarea
            value={rispostaTesto}
            onChange={(e) => onChangeTesto?.(e.target.value)}
            maxLength={1000}
            rows={6}
            disabled={sending}
            placeholder="Scrivi qui la tua risposta..."
            className="w-full bg-bg border border-border rounded-xl p-4 text-primary placeholder:text-muted focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted font-mono">
              {rispostaTesto.length} / 1000
            </span>
            <button
              type="button"
              onClick={onSubmitAperta}
              disabled={sending || rispostaTesto.trim().length === 0}
              className="bg-accent text-white px-5 py-2 rounded-xl font-mono text-sm tracking-wide hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "Invio..." : "Invia risposta"}
            </button>
          </div>
        </div>
      )}

      {domanda.tipo === "scala" && domanda.opzioniShufflate && (
        <div className="flex gap-2 justify-between">
          {domanda.opzioniShufflate.map((op, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(i)}
              disabled={sending}
              className="flex-1 bg-card border-2 border-border rounded-xl p-3 hover:border-accent transition-colors disabled:opacity-50"
            >
              <div className="text-2xl font-mono">{i + 1}</div>
              <div className="text-xs text-secondary mt-1">{op}</div>
            </button>
          ))}
        </div>
      )}

      {domanda.spiegazione && (
        <ExplanationBox
          spiegazione={domanda.spiegazione}
          isCorrect={isCorrect ?? false}
        />
      )}
    </Card>
  );
}
